/* eslint-disable */
// @ts-nocheck

import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import query from '@/api/APIServicesV2'
import { postMediaUrl } from '@/api/mediaAPI'
import useServer from '@/api/useServerMain'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import ButtonLoading from '../misc/Button'
import SpinnerLoading from '../misc/SpinnerLoading'

const fileTypeMappings: Record<string, string[]> = {
  images: ['image/jpeg', 'image/png', 'image/jpg'],
  pdf: ['application/pdf'],
  word: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  excel: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  txt: ['text/plain'],
}
const typeToTextMapping: Record<string, string> = {
  images: 'Imágenes (.jpeg, .png, .jpg)',
  pdf: 'PDF',
  word: 'Word (DOC, DOCX)',
  excel: 'Excel (XLS, XLSX)',
  txt: 'Texto (TXT)',
}

type AcceptTypes = 'images' | 'pdf' | 'word' | 'excel' | 'txt' | 'all'

interface UploadFileProps {
  onUpload: (file: File) => void
  acceptedFileTypes: AcceptTypes[]
  loading: boolean
  onClear?: () => void
  clearTrigger?: boolean
  autoUpload?: boolean
  defaultImagePath?: string
}

export default function UploadFile({
  onUpload,
  acceptedFileTypes,
  loading,
  onClear,
  clearTrigger,
  autoUpload,
  defaultImagePath,
}: UploadFileProps) {
  const [dragActive, setDragActive] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (defaultImagePath !== '' && !file) {
      setPreviewUrl(defaultImagePath ?? '')
    }
  }, [defaultImagePath, file])

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  // Clear file when clearTrigger changes
  useEffect(() => {
    if (clearTrigger) {
      clearFilePreview()
      setFile(null)
      onClear?.()
    }
  }, [clearTrigger, onClear])

  if (acceptedFileTypes.includes('all')) {
    acceptedFileTypes = Object.keys(fileTypeMappings) as AcceptTypes[]
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = () => {
    setDragActive(false)
  }
  const getAcceptedFileTypesText = () => {
    return acceptedFileTypes
      .map((type) => typeToTextMapping[type] || type)
      .join(', ')
  }

  const createFilePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    } else {
      setPreviewUrl(null)
    }
  }

  const clearFilePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      const acceptedTypes = getAcceptedFileTypesForInput().split(',')
      if (
        acceptedTypes.includes(droppedFile.type) &&
        validateImageFile(droppedFile)
      ) {
        clearFilePreview()
        setFile(droppedFile)
        createFilePreview(droppedFile)

        if (autoUpload) {
          onUpload(droppedFile)
        }
      } else {
        let errorMessage = 'Tipo de archivo no permitido.'

        if (
          droppedFile.type.startsWith('image/') &&
          !validateImageFile(droppedFile)
        ) {
          errorMessage =
            'Solo se permiten imágenes con extensión .jpeg, .jpg o .png'
        } else {
          errorMessage = `Tipo de archivo no permitido. Solo se permiten: ${getAcceptedFileTypesText()}`
        }

        toast.error(errorMessage)
      }
    }
  }

  const getAcceptedFileTypesForInput = () => {
    return acceptedFileTypes.flatMap((type) => fileTypeMappings[type]).join(',')
  }

  const validateFile = (file: File): boolean => {
    const acceptedTypes = getAcceptedFileTypesForInput().split(',')
    return acceptedTypes.includes(file.type)
  }

  const validateImageFile = (file: File): boolean => {
    // Solo validar formato específico para imágenes
    if (file.type.startsWith('image/')) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg']

      // Verificar MIME type
      const isValidMimeType = validImageTypes.includes(file.type)

      // Verificar extensión del archivo
      const fileName = file.name.toLowerCase()

      // Para imágenes, permitir archivos .jpeg, .jpg y .png
      if (file.type === 'image/jpeg') {
        // Si es JPEG, permitir tanto .jpeg como .jpg
        return fileName.endsWith('.jpeg') || fileName.endsWith('.jpg')
      } else if (file.type === 'image/png') {
        // Si es PNG, permitir archivos .png
        return fileName.endsWith('.png')
      } else if (file.type === 'image/jpg') {
        // Si el type es jpg, permitir archivos .jpg
        return fileName.endsWith('.jpg')
      }

      return false
    }
    return true // Para otros tipos de archivo, no aplicar restricción
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]

      if (validateFile(selectedFile) && validateImageFile(selectedFile)) {
        clearFilePreview()
        setFile(selectedFile)
        createFilePreview(selectedFile)

        if (autoUpload) {
          onUpload(selectedFile)
        }
      } else {
        let errorMessage = 'Tipo de archivo no permitido.'

        if (
          selectedFile.type.startsWith('image/') &&
          !validateImageFile(selectedFile)
        ) {
          errorMessage = 'Solo se permiten imágenes con extensión .jpeg o .png'
        } else {
          errorMessage = `Tipo de archivo no permitido. Solo se permiten: ${getAcceptedFileTypesText()}`
        }

        toast.error(errorMessage)
        // Limpiar el input
        e.target.value = ''
      }
    }
  }

  return (
    <div>
      <div className="space-y-4 p-6">
        <div
          className={`flex flex-col items-center gap-1 rounded-lg border-2 border-dashed p-6 ${
            dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
          }`}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {file || previewUrl ? (
            <div className="w-full space-y-3">
              <div className="text-sm text-gray-700">
                Archivo seleccionado:{' '}
                <strong>{file ? file.name : 'Imagen cargada'}</strong>
              </div>

              <div className="rounded-lg p-4">
                {previewUrl ? (
                  <div className="flex justify-center">
                    <img
                      alt="Vista previa"
                      className="max-h-48 max-w-full rounded border object-contain"
                      src={previewUrl}
                    />
                    {autoUpload && loading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/70">
                        <SpinnerLoading />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>No hay vista previa disponible</div>
                )}
              </div>
            </div>
          ) : (
            <div className={'flex w-full flex-col items-center'}>
              <FileIcon className="h-12 w-12" />
              <span className="mt-5 text-sm font-medium text-gray-500">
                Arrastra y suelta un archivo o haz clic para buscar
              </span>
              <span className="text-xs text-gray-500">
                Permitidos: {getAcceptedFileTypesText()}
              </span>
            </div>
          )}

          <Input
            accept={getAcceptedFileTypesForInput()}
            className="hidden"
            id="file"
            onChange={handleFileChange}
            type="file"
          />
          <Button
            className={'mt-5'}
            disabled={loading}
            onClick={() => document.getElementById('file')?.click()}
            variant="outline"
          >
            {file ? 'Seleccionar otro archivo' : 'Seleccionar archivo'}
          </Button>
        </div>
      </div>
      {!autoUpload && (
        <div className="flex w-full justify-end">
          {loading ? (
            <ButtonLoading
              disabled={!file}
              loading={loading}
              name="Subiendo..."
              size="default"
            />
          ) : (
            <Button
              disabled={!file}
              onClick={() => onUpload(file!)}
              size="default"
            >
              Subir archivo
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function FileIcon(props: any) {
  return (
    <svg
      {...props}
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
  )
}
